import { Badge, Box, Button, Checkbox, Editable, EditableInput, EditablePreview, Flex, HStack, Icon, Link, Portal, Skeleton, Text, Tooltip, useBreakpointValue, useToast, VStack } from '@chakra-ui/react'
import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useLocation } from 'react-router-dom'
import { X, Globe, ExternalLink, Mail, Phone, MapPin, Building2, Hash, AtSign, Pencil, FileText } from 'lucide-react'
import { OctopusIcon } from '../../../../utils/constants/icons'
import { createGraph } from '../../../../api/react/kraken/kraken'
import { parseEntityQuery, isInvestigateOnlinePrompt, isExpandPrompt, resolveExpandTarget } from '../../../../utils/functions/krakenUtils'
import { useKrakenContext } from './KrakenContext'
import KrakenChatBar from './KrakenChatBar'
import KrakenChatMessage from './KrakenChatMessage'
import type { Message } from './KrakenChatMessage'
import KrakenNetworkTree, { mergeGraphData } from './KrakenNetworkTree'
import GraphToast, { GraphToastData } from '../KrakenO2O/GraphToast'
import type { KrakenGraphResponse, KrakenStreamEvent, KrakenExpandEvent, KrakenDiscoverEvent, KrakenEntitySuggestions, KrakenEntitySuggestion, KrakenEntityUpdateType, KrakenChatSuggestion, KrakenToolsUsed, KrakenSocialPost } from '../../../../types/kraken.types'
import { useKrakenNetworkFetch, useKrakenChatStream, useKrakenExpandNode, useKrakenDiscoverWebPresence, useKrakenCreateEntity, useKrakenApplyEntitySuggestions } from '../../../../hooks/useKraken'
import handleSuccessToast from '../../../../components/common/toast/handleSuccessToast'
import handleErrorToast from '../../../../components/common/toast/handleErrorToast'
import { useAuth } from '../../../../hooks/useAuth'

const MotionBox = motion(Box)

type EntityContext = {
  type: 'item' | 'seller' | 'saw'
  id: string
  name?: string
} | null

// ─── Entity Suggestions Card ─────────────────────────────────────────────────

// Pick an icon that matches the kind of identifier a suggestion carries.
const iconForField = (field: string) => {
  const f = (field || '').toLowerCase()
  if (f === 'email') return Mail
  if (f === 'phone') return Phone
  if (f === 'address' || f === 'city' || f === 'postcode') return MapPin
  if (f === 'country') return Globe
  if (f === 'kvk' || f === 'vat' || f.includes('registration') || f === 'tax_id') return Building2
  if (['instagram', 'facebook', 'twitter', 'x', 'youtube', 'tiktok', 'threads', 'telegram', 'linkedin', 'pinterest'].includes(f)) return AtSign
  return Hash
}

const hostOf = (url: string) => {
  try {
    return new URL(url).hostname.replace(/^www\./, '')
  } catch {
    return url
  }
}

export const EntitySuggestionsCard = ({
  suggestions,
  onApply,
  onDismiss,
}: {
  suggestions: KrakenEntitySuggestions
  onApply: (entityType: KrakenEntityUpdateType, entityId: string, selected: KrakenEntitySuggestion[], appendField?: string) => void
  onDismiss: () => void
}) => {
  const [selected, setSelected] = useState<Set<number>>(
    () => new Set(suggestions.suggestions.map((_, i) => i)),
  )
  // Inline edits keyed by suggestion index; only present once the user changes a value.
  const [edits, setEdits] = useState<Record<number, string>>({})
  const [applying, setApplying] = useState(false)

  const toggle = (idx: number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(idx)) next.delete(idx)
      else next.add(idx)
      return next
    })
  }

  const valueText = (s: KrakenEntitySuggestion) =>
    Array.isArray(s.value) ? s.value.join(', ') : String(s.value)

  const handleApply = async () => {
    const items = suggestions.suggestions
      .map((s, i) => ({ s, i }))
      .filter(({ i }) => selected.has(i))
      .map(({ s, i }) => {
        const edited = edits[i]
        if (edited === undefined || edited === valueText(s)) return s
        // User tweaked the value — carry it through and let the API rebuild the
        // description line from field + value (the canned append_text is stale).
        return { ...s, value: edited, append_text: s.mode === 'append' ? '' : s.append_text }
      })
    if (!items.length) return
    setApplying(true)
    try {
      await onApply(suggestions.entity_type, suggestions.entity_id, items, suggestions.append_field)
    } finally {
      setApplying(false)
    }
  }

  const setCount = selected.size

  return (
    <Box
      bg="white"
      borderRadius="xl"
      border="1px solid"
      borderColor="gray.100"
      boxShadow="0 16px 40px -12px rgba(15,23,42,0.22), 0 2px 6px rgba(15,23,42,0.06)"
      p={4}
      mb={4}
    >
      <HStack mb={1} justify="space-between">
        <Text fontSize="13px" fontWeight="700" color="gray.800">
          Suggested updates for this {suggestions.entity_type}
        </Text>
        <Badge colorScheme="gray" variant="subtle" fontSize="10px">{setCount} selected</Badge>
      </HStack>
      <Text fontSize="11px" color="gray.500" mb={3}>
        Review, edit, and apply. Identifiers without a dedicated field are added to the description.
      </Text>

      <VStack align="stretch" spacing={2} mb={3}>
        {suggestions.suggestions.map((s, i) => {
          const isOn = selected.has(i)
          const display = edits[i] ?? valueText(s)
          return (
            <Box
              key={i}
              bg="white"
              border="1px solid"
              borderColor={isOn ? 'gray.300' : 'gray.100'}
              borderRadius="lg"
              p="8px 10px"
              opacity={isOn ? 1 : 0.6}
              transition="all 0.12s"
            >
              <HStack align="flex-start" spacing={2.5}>
                <Checkbox
                  isChecked={isOn}
                  onChange={() => toggle(i)}
                  mt="3px"
                  isDisabled={applying}
                />
                <Box flex={1} minW={0}>
                  <HStack spacing={1.5} mb="2px">
                    <Icon as={iconForField(s.field)} boxSize="13px" color="gray.400" />
                    <Text fontSize="12px" fontWeight="700" color="gray.800" textTransform="capitalize">
                      {s.field}
                    </Text>
                    <Badge colorScheme={s.confidence === 'high' ? 'green' : 'yellow'} fontSize="9px">
                      {s.confidence}
                    </Badge>
                    {s.mode === 'append' && (
                      <Tooltip label="Added to the site description" fontSize="11px">
                        <Badge colorScheme="gray" variant="subtle" fontSize="9px">
                          <HStack spacing="3px"><Icon as={FileText} boxSize="9px" /><Text>description</Text></HStack>
                        </Badge>
                      </Tooltip>
                    )}
                  </HStack>

                  <HStack spacing={1.5} align="center">
                    <Editable
                      value={display}
                      onChange={(v) => setEdits((p) => ({ ...p, [i]: v }))}
                      isDisabled={applying}
                      flex={1}
                      minW={0}
                    >
                      <Tooltip label="Click to edit" fontSize="11px" openDelay={400}>
                        <EditablePreview
                          fontSize="12px"
                          fontWeight="600"
                          color="gray.800"
                          cursor="text"
                          px="4px"
                          borderRadius="4px"
                          _hover={{ bg: 'gray.50' }}
                          maxW="100%"
                          noOfLines={2}
                        />
                      </Tooltip>
                      <EditableInput fontSize="12px" px="4px" />
                    </Editable>
                    <Icon as={Pencil} boxSize="10px" color="gray.300" />
                    {s.link && (
                      <Tooltip label={`Open ${hostOf(s.link)}`} fontSize="11px">
                        <Link href={s.link} isExternal display="inline-flex" color="gray.500" _hover={{ color: 'gray.800' }}>
                          <Icon as={ExternalLink} boxSize="13px" />
                        </Link>
                      </Tooltip>
                    )}
                  </HStack>

                  {s.reason && (
                    <Text fontSize="11px" color="gray.500" mt="3px" lineHeight="1.35">
                      {s.reason}
                    </Text>
                  )}
                  {s.source_url && (
                    <Link
                      href={s.source_url}
                      isExternal
                      fontSize="10px"
                      color="gray.400"
                      _hover={{ color: 'gray.700' }}
                      noOfLines={1}
                      display="inline-block"
                      mt="2px"
                    >
                      from {hostOf(s.source_url)} ↗
                    </Link>
                  )}
                </Box>
              </HStack>
            </Box>
          )
        })}
      </VStack>

      <HStack spacing={2}>
        <Button
          size="xs"
          bg="gray.800"
          color="white"
          _hover={{ bg: 'gray.700' }}
          _active={{ bg: 'gray.900' }}
          isLoading={applying}
          isDisabled={setCount === 0}
          onClick={handleApply}
        >
          Apply {setCount} selected
        </Button>
        <Button size="xs" variant="ghost" onClick={onDismiss} isDisabled={applying}>
          Dismiss
        </Button>
      </HStack>
    </Box>
  )
}

// ─── Main Component ──────────────────────────────────────────────────────────

const KrakenGlobalFab = () => {
  const { permissions } = useAuth()
  const { fetchNetwork } = useKrakenNetworkFetch()
  const { streamChat } = useKrakenChatStream()
  const { expandNode } = useKrakenExpandNode()
  const { discoverWeb } = useKrakenDiscoverWebPresence()
  const { createEntity } = useKrakenCreateEntity()
  const { applySuggestions } = useKrakenApplyEntitySuggestions()
  const toast = useToast()
  const [entitySuggestions, setEntitySuggestions] = useState<KrakenEntitySuggestions | null>(null)
  const handleApplySuggestions = useCallback(
    async (entityType: KrakenEntityUpdateType, entityId: string, selected: KrakenEntitySuggestion[], appendField?: string) => {
      try {
        await applySuggestions(entityType, entityId, selected, appendField)
        toast({ title: `Updated ${selected.length} field(s)`, status: 'success', duration: 3000 })
        setEntitySuggestions(null)
      } catch (err) {
        toast({ title: 'Failed to apply updates', description: String(err), status: 'error', duration: 5000 })
      }
    },
    [applySuggestions, toast],
  )
  const [expandingNodeIds, setExpandingNodeIds] = useState<Set<string>>(new Set())
  const [discoveringNodeIds, setDiscoveringNodeIds] = useState<Set<string>>(new Set())
  const [creatingEntityNodeIds, setCreatingEntityNodeIds] = useState<Set<string>>(new Set())
  const [createdEntityNodeIds, setCreatedEntityNodeIds] = useState<Set<string>>(new Set())
  const [graphToast, setGraphToast] = useState<GraphToastData | null>(null)
  const graphToastKeyRef = useRef(0)
  const showGraphToast = useCallback(
    (kind: GraphToastData['kind'], title: string, detail: string) => {
      graphToastKeyRef.current += 1
      setGraphToast({ kind, title, detail, key: graphToastKeyRef.current })
    },
    [],
  )
  const krakenContext = useKrakenContext()
  const { isOpen, openChat, closeChat, hidden: hideFab, currentEntity } = krakenContext
  const [messages, setMessages] = useState<Message[]>([])
  const [isStreaming, setIsStreaming] = useState(false)
  const [isIndexed, setIsIndexed] = useState(false)
  const [networkData, setNetworkData] = useState<KrakenGraphResponse | null>(null)
  const networkContextRef = useRef<KrakenGraphResponse | null>(null)
  const hasStartedInvestigation = useRef(false)
  const lastContextRef = useRef<string | null>(null)
  const messageIdSeq = useRef(0)
  const nextMessageId = useCallback((role: 'user' | 'assistant') => {
    messageIdSeq.current += 1
    return `${role}-${messageIdSeq.current}`
  }, [])
  const isMobile = useBreakpointValue({ base: true, md: false }, { ssr: false }) ?? false
  const [mobileView, setMobileView] = useState<'chat' | 'graph'>('chat')

  const location = useLocation()

  const urlContext = useMemo((): EntityContext => {
    const path = location.pathname
    const itemMatch = path.match(/\/react\/items\/([^/]+)/)
    if (itemMatch) {
      const id = itemMatch[1]
      if (id && !['overview', 'list', 'imports', 'exports'].some((x) => id.startsWith(x))) {
        return { type: 'item', id }
      }
    }
    const sellerMatch = path.match(/\/react\/sellers\/([^/]+)/)
    if (sellerMatch) {
      const id = sellerMatch[1]
      if (id && !['list', 'platform', 'standalone'].includes(id)) {
        return { type: 'seller', id }
      }
    }
    const sawMatch = path.match(/\/react\/saw\/sites\/([^/]+)/)
    if (sawMatch) {
      const id = sawMatch[1]
      if (id) return { type: 'saw', id }
    }
    return null
  }, [location.pathname])

  const context: EntityContext = currentEntity
    ? { type: currentEntity.type, id: currentEntity.id, name: currentEntity.name }
    : urlContext

  const fullId = currentEntity?.fullId || (context ? `${context.type}_${context.id}` : undefined)
  const contextKey = context ? `${context.type}-${context.id}` : null
  const hasStartedChat = messages.length > 0

  useEffect(() => {
    if (contextKey !== lastContextRef.current) {
      lastContextRef.current = contextKey
      hasStartedInvestigation.current = false
      setMessages([])
      setIsIndexed(false)
      setNetworkData(null)
      networkContextRef.current = null
      setEntitySuggestions(null)
    }
  }, [contextKey])

  useEffect(() => {
    if (isOpen && context && !hasStartedInvestigation.current) {
      hasStartedInvestigation.current = true
      indexEntity(context)
    }
  }, [isOpen, context])

  const indexEntity = async (ctx: NonNullable<EntityContext>) => {
    setIsStreaming(true)
    try {
      const data = await fetchNetwork(`${ctx.type}_${ctx.id}`)
      networkContextRef.current = data
      setNetworkData(data)
    } catch (error) {
      // silently handle indexing failure
    } finally {
      setIsIndexed(true)
      setIsStreaming(false)
    }
  }

  const messagesRef = useRef<Message[]>([])
  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  // Merge a graph delta from /kraken/expand/ into the live network. New nodes
  // and edges are deduped by id; existing entries keep their identity so
  // ReactFlow doesn't re-mount and the layout stays stable.
  const mergeGraphDelta = useCallback((incoming: KrakenGraphResponse | undefined) => {
    if (!incoming || !Array.isArray(incoming.nodes)) return
    const current = networkContextRef.current ?? { root: '', nodes: [], edges: [], node_count: 0, edge_count: 0 }
    const nodeById = new Map(current.nodes.map((n) => [n.id, n]))
    for (const n of incoming.nodes) {
      if (!n?.id) continue
      nodeById.set(n.id, { ...(nodeById.get(n.id) ?? {}), ...n })
    }
    const edgeKey = (e: { id?: string; from_id?: string; to_id?: string; label?: string }) =>
      e.id || `${e.from_id}->${e.to_id}:${e.label || ''}`
    const edgeByKey = new Map(current.edges.map((e) => [edgeKey(e), e]))
    for (const e of incoming.edges ?? []) {
      if (!e?.from_id || !e?.to_id) continue
      edgeByKey.set(edgeKey(e), e)
    }
    const merged: KrakenGraphResponse = {
      root: current.root || incoming.root || '',
      nodes: [...nodeById.values()],
      edges: [...edgeByKey.values()],
      node_count: nodeById.size,
      edge_count: edgeByKey.size,
    }
    networkContextRef.current = merged
    setNetworkData(merged)
  }, [])

  const [isSavingGraph, setIsSavingGraph] = useState(false)
  const handleSaveGraph = useCallback(async () => {
    if (!networkData) return
    setIsSavingGraph(true)
    try {
      await createGraph({ network_context: networkData as unknown as Record<string, unknown> })
      toast({ title: 'Graph saved', status: 'success', duration: 3000 })
    } catch {
      toast({ title: 'Failed to save graph', status: 'error', duration: 5000 })
    } finally {
      setIsSavingGraph(false)
    }
  }, [networkData, toast])

  const handleExpandNode = useCallback(
    async (vertexId: string) => {
      if (!vertexId || expandingNodeIds.has(vertexId)) return
      const before = networkContextRef.current
      const beforeNodes = before?.nodes.length ?? 0
      const beforeEdges = before?.edges.length ?? 0
      setExpandingNodeIds((prev) => new Set(prev).add(vertexId))
      let streamError: unknown = null
      try {
        await expandNode(vertexId, (event: KrakenExpandEvent) => {
          if (event.type === 'graph' && typeof event.content !== 'string') {
            mergeGraphDelta(event.content as KrakenGraphResponse)
          } else if (event.type === 'error') {
            streamError = event.content
          }
        })
      } catch (err) {
        streamError = err
        // eslint-disable-next-line no-console
        console.error('Kraken node expansion failed', err)
      } finally {
        setExpandingNodeIds((prev) => {
          const next = new Set(prev)
          next.delete(vertexId)
          return next
        })
      }

      const after = networkContextRef.current
      const addedNodes = (after?.nodes.length ?? 0) - beforeNodes
      const addedEdges = (after?.edges.length ?? 0) - beforeEdges

      if (streamError) {
        showGraphToast(
          'error',
          'Expansion failed',
          typeof streamError === 'string'
            ? streamError
            : (streamError as Error)?.message || 'See console for details.',
        )
        return
      }

      if (addedNodes === 0 && addedEdges === 0) {
        showGraphToast(
          'warning',
          'No additional connections',
          "Octopus didn't find new nodes or edges for this vertex in the graph or via web search.",
        )
      } else {
        showGraphToast(
          'success',
          'Network expanded',
          `+${addedNodes} node${addedNodes === 1 ? '' : 's'}, +${addedEdges} edge${addedEdges === 1 ? '' : 's'}.`,
        )
      }
    },
    [expandNode, expandingNodeIds, mergeGraphDelta, showGraphToast],
  )

  // /kraken/discover-web/<vertex_id>/ — Sonnet-scored Firecrawl pipeline.
  // Modelled to look identical to a regular chat turn: pushes a synthetic user
  // message ("Investigate online presence of X"), then streams status / thought
  // events into the assistant message as ThinkingChain-compatible one-liners
  // (which KrakenChatMessage auto-detects via the "Investigating…" keyword).
  // Sets the global `isStreaming` flag so the chat input bar disables exactly
  // like during a normal chat — no parallel input allowed while running.
  // Accepted matches stream into the graph as `discovery` edges in real time.
  const handleDiscoverWebNode = useCallback(
    async (
      vertexId: string,
      opts: { layers: number; categories?: string[] } = { layers: 1 },
    ) => {
      if (!vertexId || discoveringNodeIds.has(vertexId) || isStreaming) return

      // Resolve a human-friendly display name + a chip marker so the user
      // bubble shows a Site/Seller/Item pill instead of the raw UUID-style id.
      const node = networkContextRef.current?.nodes.find((n) => n.id === vertexId)
      const props = (node ?? {}) as { name?: unknown; title?: unknown; domain?: unknown; url?: unknown }
      const fromNode = [props.name, props.title, props.domain]
        .map((v) => (typeof v === 'string' ? v.trim() : ''))
        .find((s) => s) || ''
      const fallback = vertexId.split('_').slice(1).join('_').slice(0, 8) || vertexId
      const entityName = fromNode || fallback
      const fallbackUrl = typeof props.url === 'string' && props.url ? props.url : undefined
      const fallbackName = fromNode || undefined

      // Vertex-id prefix → chip type. Keep aligned with KrakenChatMessage's
      // TYPE_STYLES map (`saw`, `seller`, `item`, `entity`, `product`, `seizure`).
      const chipType =
        vertexId.startsWith('saw_')     ? 'saw'     :
        vertexId.startsWith('seller_')  ? 'seller'  :
        vertexId.startsWith('item_')    ? 'item'    :
        vertexId.startsWith('entity_')  ? 'entity'  :
        vertexId.startsWith('product_') ? 'product' :
        vertexId.startsWith('seizure_') ? 'seizure' :
        vertexId.startsWith('subcase_') ? 'subcase' :
        vertexId.startsWith('case_')    ? 'case'    :
        vertexId.startsWith('contact_') ? 'contact' : 'link'
      const chip = `[[${chipType}:${vertexId}|${entityName}]]`

      setDiscoveringNodeIds((prev) => new Set(prev).add(vertexId))
      if (!isOpen) openChat()

      // Push the synthetic user turn so the chat reads as a normal exchange.
      const userMsg: Message = {
        id: nextMessageId('user'),
        content: `Investigate online presence of ${chip}`,
        role: 'user',
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, userMsg])

      // Block input via the standard chat flag.
      setIsStreaming(true)

      const assistantId = nextMessageId('assistant')
      setMessages((prev) => [
        ...prev,
        { id: assistantId, content: '', role: 'assistant', timestamp: new Date() },
      ])

      // While running, the assistant message content is always a short
      // single-line status string starting with "Investigating: …". The
      // KrakenChatMessage component detects that keyword and renders a
      // ThinkingChain identical to the one used by normal chat streaming.
      const setRunningStep = (step: string) => {
        const safe = step.split('\n').find((l) => l.trim().length > 0)?.trim() ?? step
        // Strip leading markdown emphasis (`> `, `**`, `_`) so the thinking
        // chain shows clean plain text.
        const clean = safe.replace(/^(>+\s*|\*\*|__|_+|#+\s*)/g, '').replace(/[*_`]/g, '').trim()
        setMessages((prev) =>
          prev.map((m) =>
            m.id === assistantId ? { ...m, content: `Investigating: ${clean}` } : m,
          ),
        )
      }

      let summary = ''
      let streamError: unknown = null
      try {
        await discoverWeb(
          vertexId,
          {
            url: fallbackUrl,
            name: fallbackName,
            layers: opts.layers,
            categories: opts.categories,
          },
          (event: KrakenDiscoverEvent) => {
          if (event.type === 'status' && typeof event.content === 'string') {
            setRunningStep(event.content)
          } else if (event.type === 'thought' && typeof event.content === 'string') {
            setRunningStep(event.content)
          } else if (event.type === 'graph' && typeof event.content !== 'string') {
            mergeGraphDelta(event.content as KrakenGraphResponse)
          } else if (event.type === 'summary') {
            summary = typeof event.content === 'string' ? event.content : ''
          } else if (event.type === 'entity_suggestions' && typeof event.content !== 'string') {
            setEntitySuggestions(event.content as KrakenEntitySuggestions)
          } else if (event.type === 'error') {
            streamError = event.content
          }
        })
      } catch (err) {
        streamError = err
        // eslint-disable-next-line no-console
        console.error('Kraken web discovery failed', err)
      } finally {
        setDiscoveringNodeIds((prev) => {
          const next = new Set(prev)
          next.delete(vertexId)
          return next
        })
        setIsStreaming(false)
      }

      const finalContent = streamError
        ? `**Web discovery failed.** ${typeof streamError === 'string' ? streamError : (streamError as Error)?.message || 'See console.'}`
        : (summary || '_Web discovery complete, but no summary was returned._')

      setMessages((prev) =>
        prev.map((m) => (m.id === assistantId ? { ...m, content: finalContent } : m)),
      )
    },
    [discoverWeb, discoveringNodeIds, isOpen, isStreaming, openChat, mergeGraphDelta, nextMessageId],
  )

  // "+" on a discovered LINK node → run the direct create-entity flow headlessly
  // (no chat): spinner on the node while it scrapes + classifies + creates, then
  // a success/error toast. Reuses the /kraken/create-entity/ SSE endpoint.
  const handleCreateEntityNode = useCallback(
    async (vertexId: string) => {
      if (!vertexId || creatingEntityNodeIds.has(vertexId)) return
      const ctx = networkContextRef.current
      const node = ctx?.nodes.find((n) => n.id === vertexId)
      const props = (node ?? {}) as {
        name?: unknown; title?: unknown; domain?: unknown; url?: unknown
        screenshot?: unknown; image?: unknown; platform?: unknown
        caption?: unknown; author?: unknown; source_kind?: unknown
      }
      const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined)
      const fallbackUrl = str(props.url)
      const fallbackName = [props.name, props.title, props.domain].map(str).find(Boolean)
      // Anchor to the clicked node's parent so the new record replaces the post.
      const neighbours = (ctx?.edges ?? []).flatMap((e) =>
        e.from_id === vertexId ? [e.to_id] : e.to_id === vertexId ? [e.from_id] : [],
      )
      const anchorId =
        neighbours.find((id) => id && id !== vertexId && !/^(post_|discovery_|web_|rimg_|simg_)/.test(id)) ||
        (ctx?.root && ctx.root !== vertexId ? ctx.root : undefined)

      setCreatingEntityNodeIds((prev) => new Set(prev).add(vertexId))
      let created: { name?: string; type?: string; kind?: string } | null = null
      let errMsg = ''
      try {
        await createEntity(
          vertexId,
          {
            url: fallbackUrl,
            name: fallbackName,
            image: str(props.screenshot) || str(props.image),
            platform: str(props.platform),
            caption: str(props.caption),
            author: str(props.author),
            source_kind: str(props.source_kind),
            anchor_id: anchorId,
          },
          (event) => {
            if (event.type === 'graph' && typeof event.content !== 'string') {
              const g = event.content as KrakenGraphResponse
              setNetworkData((prev) =>
                prev ? mergeGraphData(prev, g.nodes || [], g.edges || [], g.replace_node_id) : prev,
              )
            } else if (event.type === 'entity' && typeof event.content !== 'string') {
              created = event.content as { name?: string; type?: string; kind?: string }
            } else if (event.type === 'error') {
              errMsg = typeof event.content === 'string' ? event.content : 'Create entity failed.'
            }
          },
        )
      } catch (err) {
        errMsg = err instanceof Error ? err.message : 'Create entity failed.'
      } finally {
        setCreatingEntityNodeIds((prev) => {
          const next = new Set(prev)
          next.delete(vertexId)
          return next
        })
      }

      if (created) {
        const c = created as { name?: string; type?: string; kind?: string }
        const isProduct = c.kind === 'product'
        setCreatedEntityNodeIds((prev) => new Set(prev).add(vertexId))
        handleSuccessToast(
          toast,
          isProduct ? 'Product created' : 'Entity created',
          `${c.name || (isProduct ? 'Product' : 'Entity')}${c.type && !isProduct ? ` · ${c.type}` : ''} — added to Manual Entities`,
        )
      } else {
        handleErrorToast(toast, 'Could not create', errMsg || 'Please try again.')
      }
    },
    [createEntity, creatingEntityNodeIds, toast],
  )

  const handleSendMessage = useCallback(
    async (content: string) => {
      if (!content.trim()) return

      // Typed "Investigate online …" behaves exactly like the graph node's
      // "Investigate online" button — route to web discovery / graph expansion
      // instead of a normal chat turn. Target = the referenced entity (its
      // "References:" block carries seller_/item_/saw_<uuid>), else the current
      // graph root. handleDiscoverWebNode pushes its own chip-formatted user
      // turn, so we don't add one here. (Mirrors KrakenAI.tsx for host parity.)
      if (isInvestigateOnlinePrompt(content)) {
        const targetId = parseEntityQuery(content).id || networkContextRef.current?.root || null
        if (targetId) {
          handleDiscoverWebNode(targetId, { layers: 1 })
          return
        }
        // No resolvable target → fall through to a normal chat turn.
      }

      // Typed "expand <entity>" runs the reliable node expansion (the graph's
      // Expand button) instead of a chat turn. (Mirrors KrakenAI.tsx.)
      if (isExpandPrompt(content)) {
        const targetId = resolveExpandTarget(content, networkContextRef.current?.nodes, networkContextRef.current?.root)
        if (targetId) {
          setMessages((prev) => prev.concat({ id: nextMessageId('user'), content, role: 'user', timestamp: new Date() }))
          handleExpandNode(targetId)
          return
        }
        // No node resolved → fall through to a normal chat turn.
      }

      const userMsg: Message = { id: nextMessageId('user'), content, role: 'user', timestamp: new Date() }
      setMessages((prev) => prev.map((m) => m.suggestions ? { ...m, suggestions: undefined } : m).concat(userMsg))
      setIsStreaming(true)
      const assistantId = nextMessageId('assistant')
      setMessages((prev) => [...prev, { id: assistantId, content: '', role: 'assistant', timestamp: new Date() }])
      try {
        const chatMessages = [...messagesRef.current, userMsg]
          .filter((msg) => msg.content.trim())
          .map((msg) => ({ role: msg.role as 'user' | 'assistant', content: msg.content }))
        const networkContext = networkContextRef.current
          ? {
              nodes: networkContextRef.current.nodes,
              edges: networkContextRef.current.edges,
              root: networkContextRef.current.root,
            }
          : null
        let streamedContent = ''
        await streamChat(
          { messages: chatMessages, network_context: networkContext },
          (event: KrakenStreamEvent) => {
            if (event.type === 'status') {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantId ? { ...msg, content: streamedContent || event.content as string } : msg,
                ),
              )
            } else if (event.type === 'chunk') {
              streamedContent += event.content
              setMessages((prev) =>
                prev.map((msg) => (msg.id === assistantId ? { ...msg, content: streamedContent } : msg)),
              )
            } else if (event.type === 'suggestions' && Array.isArray(event.content)) {
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantId
                    ? { ...msg, suggestions: event.content as KrakenChatSuggestion[] }
                    : msg,
                ),
              )
            } else if (event.type === 'tools_used') {
              const tu = event.content as KrakenToolsUsed
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantId ? { ...msg, toolsUsed: tu?.categories ?? [] } : msg,
                ),
              )
            } else if (event.type === 'social_posts' && Array.isArray(event.content)) {
              const posts = event.content as KrakenSocialPost[]
              setMessages((prev) =>
                prev.map((msg) =>
                  msg.id === assistantId ? { ...msg, socialPosts: [...(msg.socialPosts || []), ...posts] } : msg,
                ),
              )
            } else if (event.type === 'error') {
              setMessages((prev) =>
                prev.map((msg) => (msg.id === assistantId ? { ...msg, content: `Error: ${event.content}` } : msg)),
              )
            }
          },
        )
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred'
        setMessages((prev) =>
          prev.map((msg) => (msg.id === assistantId ? { ...msg, content: `Error: ${errorMessage}` } : msg)),
        )
      } finally {
        setIsStreaming(false)
      }
    },
    [streamChat, nextMessageId, handleDiscoverWebNode, handleExpandNode],
  )

  // Disable Chakra overlay pointer-events so they don't intercept our panels
  useEffect(() => {
    if (!isOpen) return
    const style = document.createElement('style')
    style.id = 'ocean-ai-fab-layer-fix'
    style.textContent = `
      .chakra-modal__overlay { pointer-events: none !important; }
      .chakra-modal__content-container { pointer-events: none !important; }
      .chakra-modal__content { pointer-events: auto !important; }
      [data-scope="dialog"][data-part="backdrop"] { pointer-events: none !important; }
    `
    document.head.appendChild(style)
    return () => {
      style.remove()
    }
  }, [isOpen])

  // Defeat Chakra's modal manager when ANOTHER drawer/modal is open beside the
  // Octopus drawer. Chakra puts `aria-hidden="true"` on every sibling chakra
  // portal whenever a Modal/Drawer opens (it's how aria-modal isolation works).
  // That bouncing-focus side-effect kills typing in the chat input on
  // pages that have other drawers mounted (CustomEntities ViewEntityDrawer,
  // etc.). We tag every chakra-portal containing our chat input and strip
  // aria-hidden off it as fast as it's set. The trade-off is a tiny a11y
  // regression while the Octopus drawer is visible, which is acceptable —
  // the underlying drawer is still aria-labelled and screen-reader-reachable.
  useEffect(() => {
    if (!isOpen) return
    const findKrakenPortals = (): Element[] => {
      const portals = Array.from(document.querySelectorAll('.chakra-portal'))
      return portals.filter((p) => p.querySelector('.kraken-input'))
    }
    const strip = () => {
      for (const p of findKrakenPortals()) {
        if (p.getAttribute('aria-hidden') === 'true') p.removeAttribute('aria-hidden')
      }
    }
    strip()
    const obs = new MutationObserver(strip)
    obs.observe(document.body, { attributes: true, subtree: true, attributeFilter: ['aria-hidden'] })
    return () => obs.disconnect()
  }, [isOpen])

  const showGraph = isIndexed && context

  // --- RENDER ---

  // All three entity surfaces (items, sellers, SAWs) now use the inline
  // OctopusAnalyzeCard as the trigger. The floating pill is suppressed; the
  // drawer is only opened programmatically via context.openChat().
  const fabButton =
    false ? (
      <AnimatePresence>
        <MotionBox
          position="fixed"
          bottom="24px"
          right="24px"
          zIndex={2147483647}
          cursor="pointer"
          onClick={() => openChat()}
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ type: 'spring', damping: 20, stiffness: 300 }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <HStack
            h="40px"
            px={4}
            spacing={2}
            borderRadius="full"
            bg="white"
            boxShadow="0 4px 24px -4px rgba(0, 0, 0, 0.15), 0 2px 12px -2px rgba(0, 0, 0, 0.08)"
            border="1.5px solid"
            borderColor="rgba(0, 0, 0, 0.08)"
            _hover={{ boxShadow: '0 8px 32px -4px rgba(0, 0, 0, 0.18), 0 4px 16px -2px rgba(0, 0, 0, 0.1)' }}
          >
            <OctopusIcon size={24} />
            <Text fontSize="13px" fontWeight="600" color="gray.700" whiteSpace="nowrap">
              {context.type === 'item'
                ? 'Analyze this item'
                : context.type === 'seller'
                  ? 'Analyze this seller'
                  : 'Analyze this site'}
            </Text>
          </HStack>
        </MotionBox>
      </AnimatePresence>
    ) : null

  const panels = isOpen ? (
    <>
      {/* Single backdrop — visual + click-to-close, but allows scroll-through */}
      <Box
        position="fixed"
        inset={0}
        zIndex={2147483645}
        bg={isMobile ? 'rgba(10, 10, 20, 0.55)' : 'blackAlpha.200'}
        sx={isMobile ? { backdropFilter: 'blur(6px)' } : undefined}
        onClick={() => closeChat()}
        css={{
          animation: 'fabFadeIn 0.15s ease-out',
          '@keyframes fabFadeIn': { from: { opacity: 0 }, to: { opacity: 1 } },
        }}
      />

      {/* Panel container — NO transform, NO framer-motion */}
      <Box
        position="fixed"
        top={isMobile ? '52px' : '20px'}
        right={isMobile ? '0' : '20px'}
        bottom={isMobile ? '0' : '20px'}
        left={isMobile ? '0' : undefined}
        h={isMobile ? 'auto' : 'calc(100vh - 40px)'}
        zIndex={2147483647}
        display="flex"
        flexDirection={isMobile ? 'column' : 'row'}
        gap={isMobile ? '0' : '12px'}
        pointerEvents="none"
        onClick={(e: React.MouseEvent) => e.stopPropagation()}
        bg={isMobile ? 'white' : 'transparent'}
        borderTopRadius={isMobile ? '24px' : '0'}
        overflow={isMobile ? 'hidden' : 'visible'}
        boxShadow={isMobile ? '0 -8px 32px -8px rgba(15, 23, 42, 0.18)' : 'none'}
        css={{
          animation: isMobile ? 'fabSheetUp 0.3s cubic-bezier(0.32, 0.72, 0, 1)' : 'fabPanelsIn 0.25s ease-out',
          '@keyframes fabPanelsIn': { from: { opacity: 0 }, to: { opacity: 1 } },
          '@keyframes fabSheetUp': {
            from: { opacity: 0, transform: 'translateY(24px)' },
            to: { opacity: 1, transform: 'translateY(0)' },
          },
        }}
      >
        {/* Mobile drag handle + tab switcher */}
        {isMobile && (
          <Box pointerEvents="auto" flexShrink={0} bg="white">
            {/* Drag handle */}
            <Flex justify="center" pt="10px" pb="4px">
              <Box w="40px" h="5px" borderRadius="full" bg="neutral.200" />
            </Flex>

            {/* Tab switcher row */}
            <Flex
              px="8px"
              pt="4px"
              pb="8px"
              gap="6px"
              align="center"
              borderBottomWidth="1px"
              borderColor="neutral.100"
            >
              <Flex
                flex="1"
                bg="neutral.50"
                borderRadius="10px"
                p="3px"
                gap="2px"
              >
                {([
                  { key: 'chat' as const, label: 'Octopus AI' },
                  { key: 'graph' as const, label: 'O2O Graph' },
                ]).map((tab) => {
                  const isActiveTab = mobileView === tab.key
                  return (
                    <Box
                      key={tab.key}
                      as="button"
                      flex="1"
                      py="9px"
                      borderRadius="8px"
                      fontSize="13px"
                      fontWeight={isActiveTab ? '600' : '500'}
                      color={isActiveTab ? 'neutral.900' : 'neutral.500'}
                      bg={isActiveTab ? 'white' : 'transparent'}
                      boxShadow={isActiveTab ? '0 1px 3px rgba(15, 23, 42, 0.08), 0 1px 2px rgba(15, 23, 42, 0.04)' : 'none'}
                      transition="all 0.18s ease-out"
                      sx={{ WebkitTapHighlightColor: 'transparent' }}
                      _active={{ transform: 'scale(0.98)' }}
                      onClick={() => setMobileView(tab.key)}
                    >
                      {tab.label}
                    </Box>
                  )
                })}
              </Flex>
              <Box
                as="button"
                w="40px"
                h="40px"
                display="flex"
                alignItems="center"
                justifyContent="center"
                color="neutral.500"
                borderRadius="full"
                bg="neutral.50"
                sx={{ WebkitTapHighlightColor: 'transparent' }}
                _active={{ transform: 'scale(0.94)', bg: 'neutral.100' }}
                transition="all 0.15s ease-out"
                onClick={() => closeChat()}
                aria-label="Close"
                flexShrink={0}
              >
                <X size={18} />
              </Box>
            </Flex>
          </Box>
        )}

        {/* LEFT — O2O Graph Panel */}
        {showGraph && (!isMobile || mobileView === 'graph') && (
          <Box
            width={isMobile ? '100%' : '55vw'}
            maxWidth={isMobile ? 'none' : '960px'}
            minWidth={isMobile ? 'auto' : '480px'}
            h={isMobile ? '100%' : '100%'}
            flex={isMobile ? '1' : 'none'}
            bg="white"
            boxShadow={isMobile ? 'none' : '0 8px 40px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05)'}
            borderRadius={isMobile ? '0' : '16px'}
            overflow="hidden"
            position="relative"
            display="flex"
            pointerEvents="auto"
            onWheel={(e: React.WheelEvent) => e.stopPropagation()}
            flexDirection="column"
          >
            <HStack px={4} pt={3} pb={2} spacing={2}>
              <Text fontSize="11px" fontWeight="700" color="gray.500" letterSpacing="0.05em">
                O2O
              </Text>
              {networkData && networkData.nodes.length > 0 ? (
                <HStack spacing={1}>
                  <Box w="6px" h="6px" borderRadius="full" bg="green.400" />
                  <Text fontSize="10px" color="neutral.400" fontWeight="500">
                    {networkData.nodes?.length ?? 0} nodes &middot; {networkData.edges?.length ?? 0} edges
                  </Text>
                </HStack>
              ) : (
                <HStack spacing={1}>
                  <Box w="6px" h="6px" borderRadius="full" bg="gray.300" />
                  <Text fontSize="10px" color="neutral.400" fontWeight="500">
                    No network data
                  </Text>
                </HStack>
              )}
            </HStack>
            <Box flex={1} minH={0} display="flex" flexDirection="column">
              {networkData && networkData.nodes.length > 0 ? (
                <KrakenNetworkTree
                  networkData={networkData}
                  isLoading={false}
                  rootId={fullId}
                  rootType={context?.type}
                  onExpandNode={handleExpandNode}
                  expandingNodeIds={expandingNodeIds}
                  onDiscoverWebNode={handleDiscoverWebNode}
                  discoveringNodeIds={discoveringNodeIds}
                  onCreateEntityNode={handleCreateEntityNode}
                  creatingEntityNodeIds={creatingEntityNodeIds}
                  createdEntityNodeIds={createdEntityNodeIds}
                  onSaveGraph={handleSaveGraph}
                  isSavingGraph={isSavingGraph}
                  onMergeGraphData={(newNodes, newEdges) => {
                    setNetworkData((prev) => prev ? mergeGraphData(prev, newNodes, newEdges) : prev)
                  }}
                />
              ) : (
                <VStack flex={1} justify="center" align="center" spacing={2} color="gray.400">
                  <Globe size={32} strokeWidth={1.2} />
                  <Text fontSize="13px" fontWeight="500">
                    Not yet indexed in the network graph
                  </Text>
                  <Text fontSize="11px" color="gray.300">
                    Chat with Octopus AI using the panel on the right
                  </Text>
                </VStack>
              )}
            </Box>
            <GraphToast toast={graphToast} onClose={() => setGraphToast(null)} />
          </Box>
        )}

        {/* RIGHT — Chat Panel — FLAT structure: panel > scrollable | footer */}
        <Box
          w={isMobile ? '100%' : '25vw'}
          minW={isMobile ? 'auto' : '380px'}
          maxW={isMobile ? 'none' : '480px'}
          h={isMobile ? '100%' : '100%'}
          flex={isMobile ? '1' : 'none'}
          display={!isMobile || mobileView === 'chat' ? 'flex' : 'none'}
          bg="white"
          boxShadow={isMobile ? 'none' : '0 8px 40px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.05)'}
          borderRadius={isMobile ? '0' : '16px'}
          position="relative"
          pointerEvents="auto"
          onWheel={(e: React.WheelEvent) => e.stopPropagation()}
          sx={{
            flexDirection: 'column',
            overflow: 'hidden',
            paddingBottom: isMobile ? 'env(safe-area-inset-bottom, 0px)' : undefined,
          }}
        >
          {/* Close button (absolute — desktop only; mobile uses tab-bar close) */}
          {!isMobile && (
            <Box
              position="absolute"
              top={3}
              right={3}
              zIndex={20}
              cursor="pointer"
              color="gray.400"
              _hover={{ color: 'gray.600' }}
              p={1}
              borderRadius="full"
              onClick={() => closeChat()}
            >
              <X size={16} />
            </Box>
          )}
          {/* Accent line (absolute — does not affect flex) */}
          <Box
            position="absolute"
            top={0}
            left={0}
            bottom={0}
            w="3px"
            bg="gray.300"
            borderTopLeftRadius="16px"
            borderBottomLeftRadius="16px"
          />
          {/* Dotted background (absolute — does not affect flex) */}
          <Box
            position="absolute"
            inset={0}
            zIndex={0}
            pointerEvents="none"
            bgImage="radial-gradient(rgba(0, 0, 0, 0.05) 1.5px, transparent 1.5px)"
            bgSize="12px 12px"
          />
          <Box
            position="absolute"
            inset={0}
            zIndex={1}
            pointerEvents="none"
            bgImage="linear-gradient(to top, transparent 0%, rgba(255,255,255,0.6) 100%)"
          />

          {/* === CONTENT AREA — position:relative so the absolute child fills it === */}
          <Box
            sx={{
              flex: '1 1 0%',
              minHeight: 0,
              position: 'relative',
              zIndex: 2,
            }}
          >
            {/* Absolute-positioned scroll container — bypasses flex min-height issues */}
            <Box
              position="absolute"
              top={0}
              left={0}
              right={0}
              bottom={0}
              overflowY="auto"
              overflowX="hidden"
              css={{
                WebkitOverflowScrolling: 'touch',
                '&::-webkit-scrollbar': { width: '6px' },
                '&::-webkit-scrollbar-track': { background: 'transparent' },
                '&::-webkit-scrollbar-thumb': { background: 'rgba(124, 58, 237, 0.15)', borderRadius: '3px' },
                '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(124, 58, 237, 0.25)' },
              }}
            >
              {/* Indexing skeleton */}
              {context && isStreaming && !isIndexed && (
                <Box mx={3} mt={3} mb={2}>
                  <Box
                    bg="white"
                    borderRadius="12px"
                    boxShadow="0 2px 12px rgba(0,0,0,0.06), 0 0 0 1px rgba(0,0,0,0.04)"
                    p={4}
                  >
                    <HStack spacing={2} mb={3}>
                      <Box
                        w="8px"
                        h="8px"
                        borderRadius="full"
                        bg="gray.400"
                        css={{
                          animation: 'pulse 1.5s ease-in-out infinite',
                          '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
                        }}
                      />
                      <Text fontSize="12px" color="gray.500" fontWeight="500">
                        Indexing {context.type}...
                      </Text>
                    </HStack>
                    <VStack spacing={2} align="stretch">
                      <Skeleton height="14px" borderRadius="6px" />
                      <Skeleton height="14px" borderRadius="6px" w="75%" />
                      <Skeleton height="14px" borderRadius="6px" w="55%" />
                    </VStack>
                  </Box>
                </Box>
              )}

              {hasStartedChat ? (
                <Box px={4} py={6}>
                  {messages.map((message, index) => (
                    <Box key={message.id} mb={4}>
                      <KrakenChatMessage
                        message={message.content}
                        isUser={message.role === 'user'}
                        isStreaming={isStreaming && index === messages.length - 1 && message.role === 'assistant'}
                        onSendMessage={handleSendMessage}
                        onInvestigateOnline={handleDiscoverWebNode}
                        suggestions={message.suggestions}
                      />
                    </Box>
                  ))}
                  {entitySuggestions && entitySuggestions.suggestions.length > 0 && (
                    <EntitySuggestionsCard
                      suggestions={entitySuggestions}
                      onApply={handleApplySuggestions}
                      onDismiss={() => setEntitySuggestions(null)}
                    />
                  )}
                  <Box h="120px" />
                </Box>
              ) : (
                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  justifyContent="center"
                  h="100%"
                  px={6}
                  py={8}
                >
                  <HStack spacing={2} mb={2}>
                    <OctopusIcon size={44} />
                    <Text fontSize="20px" fontWeight="700" color="gray.700" letterSpacing="-0.02em">
                      OCTOPUS AI
                    </Text>
                  </HStack>
                </Box>
              )}
            </Box>
          </Box>

          {/* === FIXED FOOTER — chat input bar === */}
          <Box
            flexShrink={0}
            position="relative"
            zIndex={3}
            _before={{
              content: '""',
              position: 'absolute',
              top: '-60px',
              left: 0,
              right: 0,
              h: '60px',
              pointerEvents: 'none',
              bgImage: 'linear-gradient(to bottom, transparent 0%, rgba(255,255,255,0.95) 70%, white 100%)',
            }}
          >
            <KrakenChatBar
              onSendMessage={handleSendMessage}
              disabled={hasStartedChat ? isStreaming : isStreaming && !isIndexed}
            />
          </Box>
        </Box>
      </Box>
    </>
  ) : null

  if (!permissions?.isSuperAdmin) return null

  return (
    <Portal appendToParentPortal={false}>
      {fabButton}
      {panels}
    </Portal>
  )
}

export default KrakenGlobalFab
